import type { CpvCode } from '../api/cpvCodes'
import type { ProcurementFilters } from '../api/procurements'
import type { Locale, TranslateParams } from '../i18n'
import { cpvLabel } from './cpvLabel'

export interface FilterSummaryItem {
  label: string
  value: string
}

type Translate = (key: string, params?: TranslateParams) => string

/**
 * Turns a saved filter's raw query fields (e.g. `country: 'HRV'`) into
 * label/value pairs a user can read (e.g. `Country: Croatia`), reusing the
 * same i18n labels the "New filter" form shows for each field so the two
 * stay consistent. `cpvCatalog` is optional — the CPV entry is shown with
 * just its code until the catalogue has loaded (or if it fails to).
 * `locale` picks the CPV description language (English fallback).
 */
export function buildFilterSummary(
  f: ProcurementFilters,
  t: Translate,
  cpvCatalog: readonly CpvCode[] = [],
  locale: Locale = 'en',
): FilterSummaryItem[] {
  const items: FilterSummaryItem[] = []

  if (f.search) {
    items.push({ label: t('views.savedFilters.form.search'), value: `"${f.search}"` })
  }
  if (f.status) {
    items.push({
      label: t('views.savedFilters.form.status'),
      value: t(`views.procurements.status.${f.status}`),
    })
  }
  if (f.procurementType) {
    items.push({
      label: t('views.savedFilters.form.type'),
      value: t(`views.procurements.type.${f.procurementType}`),
    })
  }
  if (f.country) {
    items.push({
      label: t('views.procurements.filters.country'),
      value: t(`countries.${f.country}`),
    })
  }
  if (f.minAiScore !== undefined && f.minAiScore !== null) {
    items.push({ label: t('views.savedFilters.form.minScore'), value: String(f.minAiScore) })
  }
  if (f.cpv) {
    const match = cpvCatalog.find((c) => c.code === f.cpv)
    items.push({
      label: t('views.savedFilters.form.cpv'),
      value: match ? `${f.cpv} — ${cpvLabel(match, locale)}` : f.cpv,
    })
  }

  return items
}

export function filterSummaryText(items: readonly FilterSummaryItem[]): string {
  return items.map((i) => `${i.label}: ${i.value}`).join(' · ')
}
